import classes from '../ccf-call-conference.module.scss';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { FiberManualRecord, Close } from '@mui/icons-material';
import { CallIcons, User } from '../ccf-call-conference.slice';
import {
  CcfCallControls,
  CcfCallMuteIcon,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import React from 'react';

interface UsersInConferenceProps {
  users: User[];
  icons: CallIcons[];
}

const useStyles = makeStyles(() =>
  createStyles({
    buttonRoot: {
      '&.MuiBox-root': {
        padding: '0px',
      },
    },
  })
);

/**
 * Function for showing hover box in conference
 * @param param0 - user
 * @example <HoverBox />
 * @returns
 */
const HoverBox = ({ user }: { user: User }) => {
  const [hover, updateHover] = React.useState(false);
  /**
   * Function to be used to update hover state on mouse enter
   * @example onMouseEnter()
   */
  const onMouseEnter = () => updateHover(true);
  /**
   * Function to be used to update hover state on mouse leave
   * @example onMouseLeave()
   */
  const onMouseLeave = () => updateHover(false);
  const styles = useStyles();

  return (
    <Box
      display="flex"
      className={`${styles.buttonRoot} ${classes.callControlHover}`}
      alignItems="center"
      py={1}
      width={180}
      marginTop={2}
      padding={0}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      bgcolor={hover ? 'background.default' : 'background.paper'}
    >
      <IconButton size="small">
        <FiberManualRecord
          htmlColor="grey"
          classes={
            user.designation === 'Customer'
              ? { root: classes.userInConferenceIcon }
              : { root: classes.userInConferenceIconInactive }
          }
        />
      </IconButton>
      <Box maxWidth={hover ? 40 : '100%'}>
        <Typography
          variant="subtitle2"
          noWrap
          classes={{ root: classes.userInConferenceTypography }}
        >
          {user.name}
        </Typography>
        <Typography variant="caption" component="div" noWrap>
          {user.designation}
        </Typography>
      </Box>
      {hover && (
        <Box
          display="flex"
          marginLeft={1}
          boxShadow={1}
          px={1}
          py={0.5}
          borderRadius={4}
          bgcolor="background.paper"
        >
          <IconButton size="small" className={classes.conferenceHoverBtn}>
            <CcfCallMuteIcon viewBox="0 0 20 20" />
          </IconButton>
          <Divider
            orientation="vertical"
            classes={{ root: classes.verticalDivider }}
          />
          <IconButton size="small" className={classes.conferenceHoverBtn}>
            <Close />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

/**
 * Component to be used to show user in conference
 * @param props - UsersInConferenceProps
 * @example - <UsersInConference />
 * @returns
 */
export function UsersInConference(props: UsersInConferenceProps) {
  const { icons, users } = props;

  return users.length > 0 ? (
    <>
      <CcfCallControls icons={icons} />
      <Box
        px={1}
        maxHeight={200}
        className={classes.UsersInConferenceContainer}
      >
        <CcfTypography
          translationKey="participantX"
          extraArgs={{ format: [users.length] }}
          gutterBottom
          variant="caption"
        />
        {users.map((user) => (
          <HoverBox user={user} />
        ))}
      </Box>
    </>
  ) : null;
}
